import React, { useEffect } from "react";
import { motion } from "framer-motion";
import {
  FileText,
  CheckCircle2,
  Users,
  Ban,
  MapPin,
  Award,
  PenLine,
  Wifi,
  AlertTriangle,
  ShieldCheck,
  Power,
  Gavel,
  Mail,
  ArrowLeft,
  Clock,
  Scale,
  Handshake,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { updateSEO, buildBreadcrumbSchema } from "../utils/seo";

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.5 },
  }),
};

const sections = [
  {
    icon: CheckCircle2,
    color: "text-emerald-600",
    bg: "bg-emerald-50",
    number: "01",
    title: "Acceptance of Terms",
    content:
      'By accessing and using the SaferSavvy application ("App"), you accept and agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, you must not use the App.',
  },
  {
    icon: FileText,
    color: "text-blue-600",
    bg: "bg-blue-50",
    number: "02",
    title: "Description of Service",
    content:
      "SaferSavvy is a bus transportation management system designed for university students, staff, and authorized personnel. The App provides:",
    bullets: [
      "Real-time bus location tracking",
      "Route information and schedules",
      "Bus arrival notifications",
      "User account management",
      "Administrative tools for transport management",
    ],
  },
  {
    icon: Users,
    color: "text-indigo-600",
    bg: "bg-indigo-50",
    number: "03",
    title: "User Accounts",
    subsections: [
      {
        subtitle: "Account Registration",
        text: "To use certain features of the App, you must register for an account. You agree to provide accurate, current, and complete information during registration and to update such information to keep it accurate, current, and complete.",
      },
      {
        subtitle: "Account Security",
        text: "You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You agree to immediately notify us of any unauthorized use of your account.",
      },
      {
        subtitle: "Account Eligibility",
        text: "The App is intended for use by university students, staff, and authorized personnel only. You must be at least 13 years old to use the App. By using the App, you represent that you meet these eligibility requirements.",
      },
    ],
  },
  {
    icon: Ban,
    color: "text-red-600",
    bg: "bg-red-50",
    number: "04",
    title: "Acceptable Use",
    content: "You agree not to:",
    bullets: [
      "Use the App for any illegal purpose or in violation of any laws",
      "Attempt to gain unauthorized access to the App or its systems",
      "Interfere with or disrupt the App or servers connected to the App",
      "Transmit any viruses, malware, or harmful code",
      "Impersonate any person or entity or misrepresent your affiliation",
      "Collect or harvest information about other users",
      "Use the App to harass, abuse, or harm others",
      "Reverse engineer, decompile, or disassemble the App",
    ],
  },
  {
    icon: MapPin,
    color: "text-orange-600",
    bg: "bg-orange-50",
    number: "05",
    title: "Location Services",
    content:
      "The App requires access to your device's location services to provide bus tracking features. By enabling location services, you consent to the collection and use of your location data as described in our Privacy Policy. You may disable location services at any time through your device settings, though this may limit App functionality.",
  },
  {
    icon: Award,
    color: "text-purple-600",
    bg: "bg-purple-50",
    number: "06",
    title: "Intellectual Property",
    content:
      "The App and its original content, features, and functionality are owned by SaferSavvy and are protected by international copyright, trademark, patent, trade secret, and other intellectual property laws. You may not copy, modify, distribute, sell, or lease any part of the App without our prior written consent.",
  },
  {
    icon: PenLine,
    color: "text-teal-600",
    bg: "bg-teal-50",
    number: "07",
    title: "User Content",
    content:
      "You retain ownership of any content you submit through the App. By submitting content, you grant us a worldwide, non-exclusive, royalty-free license to use, reproduce, modify, and distribute such content for the purpose of operating and improving the App.",
  },
  {
    icon: Wifi,
    color: "text-cyan-600",
    bg: "bg-cyan-50",
    number: "08",
    title: "Service Availability",
    content:
      "We strive to provide continuous access to the App but do not guarantee uninterrupted or error-free service. The App may be unavailable due to maintenance, updates, or circumstances beyond our control. We reserve the right to modify, suspend, or discontinue any part of the App at any time.",
  },
  {
    icon: AlertTriangle,
    color: "text-amber-600",
    bg: "bg-amber-50",
    number: "09",
    title: "Limitation of Liability",
    content:
      "To the maximum extent permitted by law, SaferSavvy shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly, or any loss of data, use, goodwill, or other intangible losses resulting from your use of the App.",
  },
  {
    icon: ShieldCheck,
    color: "text-green-600",
    bg: "bg-green-50",
    number: "10",
    title: "Indemnification",
    content:
      "You agree to indemnify and hold harmless SaferSavvy, its officers, directors, employees, and agents from any claims, damages, losses, liabilities, and expenses (including legal fees) arising out of or relating to your use of the App or violation of these Terms.",
  },
  {
    icon: Power,
    color: "text-rose-600",
    bg: "bg-rose-50",
    number: "11",
    title: "Termination",
    content:
      "We may terminate or suspend your account and access to the App immediately, without prior notice, for any reason, including if you breach these Terms. Upon termination, your right to use the App will cease immediately.",
  },
  {
    icon: Scale,
    color: "text-slate-600",
    bg: "bg-slate-100",
    number: "12",
    title: "Changes to Terms",
    content:
      'We reserve the right to modify these Terms at any time. We will notify users of any material changes by posting the updated Terms in the App and updating the "Last Updated" date. Your continued use of the App after such modifications constitutes acceptance of the updated Terms.',
  },
  {
    icon: Gavel,
    color: "text-violet-600",
    bg: "bg-violet-50",
    number: "13",
    title: "Governing Law",
    content:
      "These Terms shall be governed by and construed in accordance with the laws of Pakistan, without regard to its conflict of law provisions. Any disputes arising from these Terms shall be subject to the exclusive jurisdiction of the courts of Lahore, Pakistan.",
  },
];

const TermsOfService: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isStandalone = location.pathname === "/terms-of-service";

  /* ── SEO: Terms of Service page meta ── */
  useEffect(() => {
    updateSEO({
      title: "Terms of Service — SaferSavvy Usage Agreement",
      description:
        "Read SaferSavvy's Terms of Service covering account usage, acceptable use policies, intellectual property, location services, and governing law for our campus transport platform.",
      canonical: "/terms-of-service",
      keywords: "SaferSavvy terms of service, usage agreement, campus transport terms, user agreement",
      jsonLd: buildBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Terms of Service", url: "/terms-of-service" },
      ]),
    });
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {isStandalone && <Navbar />}

      {/* Hero Section */}
      <div
        className={`relative bg-gradient-to-br from-primary-700 via-primary-600 to-primary-800 ${
          isStandalone ? "pt-32 pb-16" : "pt-8 pb-12"
        }`}
      >
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-white/5 rounded-full" />
          <div className="absolute bottom-0 -left-20 w-60 h-60 bg-white/5 rounded-full" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-white/[0.02] rounded-full" />
        </div>

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 text-center">
          {!isStandalone && (
            <button
              onClick={() => navigate(-1)}
              className="absolute left-4 sm:left-6 top-0 flex items-center gap-2 text-white/80 hover:text-white transition-colors text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
          )}

          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 200, damping: 15 }}
            className="w-16 h-16 bg-white/10 backdrop-blur rounded-2xl flex items-center justify-center mx-auto mb-6"
          >
            <FileText className="w-8 h-8 text-white" />
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-3xl sm:text-4xl font-bold text-white mb-3"
          >
            Terms of Service
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-white/70 text-sm sm:text-base max-w-xl mx-auto"
          >
            Please read these terms carefully before using SaferSavvy. By
            accessing our services, you agree to be bound by these terms.
          </motion.p>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="mt-4 inline-flex items-center gap-2 bg-white/10 backdrop-blur px-4 py-2 rounded-full text-white/80 text-xs"
          >
            <Clock className="w-3.5 h-3.5" />
            Last Updated: January 2024
          </motion.div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
        {/* Acceptance Banner */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-4 mb-8 flex items-start gap-3"
        >
          <Handshake className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-800">
              Agreement Notice
            </p>
            <p className="text-xs text-amber-700 mt-0.5">
              By using the SaferSavvy App, you acknowledge that you have read,
              understood, and agree to be bound by these Terms of Service.
            </p>
          </div>
        </motion.div>

        <div className="space-y-6">
          {sections.map((section, idx) => {
            const Icon = section.icon;
            return (
              <motion.div
                key={section.number}
                custom={idx}
                variants={fadeIn}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-50px" }}
                className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow overflow-hidden"
              >
                {/* Section Header */}
                <div className="flex items-center gap-4 px-6 py-5 border-b border-gray-50">
                  <div
                    className={`w-11 h-11 ${section.bg} rounded-xl flex items-center justify-center flex-shrink-0`}
                  >
                    <Icon className={`w-5 h-5 ${section.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-gray-300">
                        {section.number}
                      </span>
                      <h2 className="text-lg font-semibold text-gray-900">
                        {section.title}
                      </h2>
                    </div>
                  </div>
                </div>

                {/* Section Body */}
                <div className="px-6 py-5">
                  {section.content && (
                    <p className="text-sm text-gray-600 leading-relaxed mb-4">
                      {section.content}
                    </p>
                  )}

                  {section.bullets && (
                    <ul className="space-y-2.5 ml-1">
                      {section.bullets.map((b, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <div className="w-1.5 h-1.5 rounded-full bg-primary-500 mt-2 flex-shrink-0" />
                          <span className="text-sm text-gray-600">{b}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  {section.subsections?.map((sub, sIdx) => (
                    <div
                      key={sIdx}
                      className={`${sIdx > 0 ? "mt-6 pt-5 border-t border-gray-50" : ""}`}
                    >
                      <h3 className="text-sm font-semibold text-gray-800 mb-2">
                        {sub.subtitle}
                      </h3>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        {sub.text}
                      </p>
                    </div>
                  ))}
                </div>
              </motion.div>
            );
          })}

          {/* Contact Card */}
          <motion.div
            custom={sections.length}
            variants={fadeIn}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="bg-gradient-to-br from-primary-600 to-primary-700 rounded-2xl p-8 text-center"
          >
            <div className="w-14 h-14 bg-white/10 backdrop-blur rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Mail className="w-7 h-7 text-white" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">
              Questions About Our Terms?
            </h3>
            <p className="text-white/70 text-sm mb-5 max-w-md mx-auto">
              If you have any questions about these Terms of Service, please
              contact us.
            </p>
            <div className="inline-flex flex-col sm:flex-row gap-3">
              <a
                href="mailto:info@safersavvy.tech"
                className="bg-white text-primary-700 font-medium px-6 py-2.5 rounded-xl text-sm hover:bg-white/90 transition-colors inline-flex items-center justify-center gap-2"
              >
                <Mail className="w-4 h-4" />
                info@safersavvy.tech
              </a>
            </div>
            <p className="text-white/50 text-xs mt-4">
              University of Engineering and Technology, Lahore
            </p>
          </motion.div>
        </div>
      </div>

      {isStandalone && <Footer />}
    </div>
  );
};

export default TermsOfService;
